import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { HelpTip } from '../components/HelpTip';
import type { Flujo } from './flujo-caja/types';
import { Proyeccion } from './flujo-caja/Proyeccion';
import { CuentasTab } from './flujo-caja/CuentasTab';
import { GastosTab } from './flujo-caja/GastosTab';

// ============================================================
// Página
// ============================================================

export function FlujoCajaPage() {
  const [tab, setTab] = useState<'proyeccion' | 'cuentas' | 'gastos'>('proyeccion');
  const [semanas, setSemanas] = useState(13);
  const [incluirComprometido, setIncluirComprometido] = useState(true);
  const { data, loading, error, refetch } = useApi<Flujo>(`/api/v3/finanzas/flujo-caja?semanas=${semanas}&incluirComprometido=${incluirComprometido}`);

  const tabs = [
    { key: 'proyeccion' as const, label: 'Proyección' },
    { key: 'cuentas' as const, label: 'Cuentas y saldos' },
    { key: 'gastos' as const, label: 'Gastos recurrentes' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 inline">Flujo de caja</h1>
        <HelpTip tips={[
          'Proyección semanal (lunes a domingo) de lo que entra y sale de caja, desde la semana actual',
          'Saldo inicial: el último saldo cargado de cada cuenta activa, tomado como saldo de hoy. Cargá saldos actualizados en "Cuentas y saldos"',
          'Egresos: cuotas impagas de facturas de compra, vencimientos, gastos recurrentes y (opcional) lo pendiente de facturar de las OCs abiertas',
          'Ingresos: saldo de facturas de venta a su vencimiento, ingresos de caja no confirmados y cobros de MercadoLibre en la fecha real en que MercadoPago libera el dinero',
          'Cuenta MercadoPago: el saldo disponible se lee automáticamente de MercadoPago (lo no liberado se proyecta como cobro)',
          'Lo impago con fecha pasada figura como atrasado y se suma a la semana actual',
          'Hacé clic en un importe para ver el detalle',
        ]} />
        <p className="text-slate-500 text-sm mt-1">Saldo proyectado por semana según pagos y cobros pendientes</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200" role="tablist">
        {tabs.map(t => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.key ? 'border-brand-500 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'proyeccion' && (
        <Proyeccion data={data} loading={loading} error={error} semanas={semanas} setSemanas={setSemanas}
          incluirComprometido={incluirComprometido} setIncluirComprometido={setIncluirComprometido} />
      )}
      {tab === 'cuentas' && <CuentasTab onCambio={refetch} diasSaldoViejo={data?.diasSaldoViejo} />}
      {tab === 'gastos' && <GastosTab onCambio={refetch} />}
    </div>
  );
}
